
import { memo, useId } from 'react';
import {
    BaseEdge,
    EdgeLabelRenderer,
    getBezierPath,
    type EdgeProps,
    MarkerType,
} from '@xyflow/react';
import { AlertTriangle } from 'lucide-react';
import { type WorkflowEdgeData, type EdgeType } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

// CSS keyframes for edge animation - injected once
const animationStyles = `
@keyframes flowEdge {
    from { stroke-dashoffset: 24; }
    to { stroke-dashoffset: 0; }
}
@keyframes flowEdgeSlow {
    from { stroke-dashoffset: 24; }
    to { stroke-dashoffset: 0; }
}
`;

// Inject styles once
if (typeof document !== 'undefined') {
    const styleId = 'workflow-edge-animations';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = animationStyles;
        document.head.appendChild(style);
    }
}

/**
 * Get edge styling based on edge type.
 */
function getEdgeStyle(edgeType: EdgeType, selected: boolean): {
    stroke: string;
    strokeWidth: number;
    strokeDasharray?: string;
    className?: string;
} {
    const baseWidth = selected ? 1.5 : 1;

    switch (edgeType) {
        case 'catch':
            // Error/Catch edge: red dashed
            return {
                stroke: '#ef4444',
                strokeWidth: baseWidth,
                strokeDasharray: '5 3',
            };
        case 'choice':
            // Choice branch: dark blue dashed
            return {
                stroke: '#1e40af',
                strokeWidth: baseWidth,
                strokeDasharray: '6 3',
            };
        case 'default':
            // Choice default branch: dark blue dashed
            return {
                stroke: '#1e40af',
                strokeWidth: baseWidth,
                strokeDasharray: '8 4',
            };
        case 'parallel':
            // Parallel branch: black
            return {
                stroke: '#111827',
                strokeWidth: baseWidth,
            };
        case 'dynamic':
            // Dynamic Next (JSONPath-resolved at runtime): teal dashed
            return {
                stroke: '#0891b2',
                strokeWidth: baseWidth,
                strokeDasharray: '4 4',
            };
        case 'normal':
        default:
            // Normal Next edge: dark gray solid
            return {
                stroke: selected ? '#3b82f6' : '#374151',
                strokeWidth: baseWidth,
            };
    }
}

/**
 * Parse a choice condition label into variable and condition parts.
 * Labels come in format: "variable == value" or "variable > 5" etc.
 * Examples:
 * - "status == approved" -> { variable: "status", condition: "== approved" }
 * - "count > 5" -> { variable: "count", condition: "> 5" }
 */
function parseChoiceLabel(label: string): { variable: string; condition: string } | null {
    // Match patterns like: variable operator value
    // Operators: ==, >, <, >=, <=, is null, is present
    const match = label.match(/^([.\w[\]]+)\s+(==|>|<|>=|<=|is null|is present)\s*(.*)$/);
    if (match) {
        const variable = match[1];
        const operator = match[2];
        const value = match[3];
        const condition = value ? `${operator} ${value}` : operator;
        return { variable, condition };
    }
    
    // Fallback: split on first space if no operator matched
    const spaceIndex = label.indexOf(' ');
    if (spaceIndex > 0) {
        return {
            variable: label.substring(0, spaceIndex),
            condition: label.substring(spaceIndex + 1),
        };
    }
    
    return null;
}

/**
 * Get label styling based on edge type.
 */
function getLabelStyle(edgeType: EdgeType): {
    bg: string;
    text: string;
    border: string;
} {
    switch (edgeType) {
        case 'catch':
            return {
                bg: 'bg-red-100',
                text: 'text-red-700',
                border: 'border-red-200',
            };
        case 'choice':
            return {
                bg: 'bg-blue-100',
                text: 'text-blue-800',
                border: 'border-blue-300',
            };
        case 'default':
            return {
                bg: 'bg-blue-50',
                text: 'text-blue-700',
                border: 'border-blue-200',
            };
        case 'parallel':
            return {
                bg: 'bg-gray-100',
                text: 'text-gray-900',
                border: 'border-gray-300',
            };
        case 'dynamic':
            return {
                bg: 'bg-cyan-100',
                text: 'text-cyan-800',
                border: 'border-cyan-300',
            };
        default:
            return {
                bg: 'bg-slate-100',
                text: 'text-slate-600',
                border: 'border-slate-200',
            };
    }
}

/**
 * WorkflowEdge - Custom edge component with type-based styling.
 * 
 * Edge Types:
 * - normal: Standard Next transition (solid slate arrow)
 * - catch: Error/Catch transition (dashed red arrow)
 * - choice: Choice branch (amber arrow with condition label)
 * - default: Choice Default branch (dashed amber arrow)
 * - parallel: Parallel branch (purple arrow)
 */
export const WorkflowEdge = memo(function WorkflowEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected = false,
    markerEnd,
}: EdgeProps) {
    const edgeData = data as WorkflowEdgeData | undefined;
    const edgeType = edgeData?.edgeType ?? 'normal';
    const label = edgeData?.label;
    const isDefault = edgeData?.isDefault;

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const edgeStyle = getEdgeStyle(edgeType, selected);
    const labelStyle = getLabelStyle(edgeType);

    // Determine marker color based on edge type
    const markerColor = edgeType === 'catch' ? '#ef4444' : 
                        edgeType === 'choice' || edgeType === 'default' ? '#1e40af' :
                        edgeType === 'parallel' ? '#111827' :
                        edgeType === 'dynamic' ? '#0891b2' :
                        '#374151';

    // Animation settings for choice and dynamic edges
    const isChoiceEdge = edgeType === 'choice' || edgeType === 'default';
    const isDynamicEdge = edgeType === 'dynamic';
    const isAnimatedEdge = isChoiceEdge || isDynamicEdge;
    const animationDuration = edgeType === 'choice' ? '0.4s' : edgeType === 'dynamic' ? '0.6s' : '0.8s';

    return (
        <>
            {/* Animated overlay for choice and dynamic edges */}
            {isAnimatedEdge && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke={edgeStyle.stroke}
                    strokeWidth={edgeStyle.strokeWidth}
                    strokeDasharray="8 16"
                    strokeLinecap="round"
                    style={{
                        animation: `flowEdge ${animationDuration} linear infinite`,
                    }}
                    markerEnd={markerEnd}
                />
            )}
            <BaseEdge
                id={id}
                path={edgePath}
                style={{
                    stroke: edgeStyle.stroke,
                    strokeWidth: edgeStyle.strokeWidth,
                    strokeDasharray: isAnimatedEdge ? undefined : edgeStyle.strokeDasharray,
                    opacity: isAnimatedEdge ? 0.3 : 1,
                }}
                markerEnd={isAnimatedEdge ? undefined : markerEnd}
            />

            {/* Render label if present */}
            {label && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            pointerEvents: 'all',
                            zIndex: 1000, // Ensure labels render above nodes (map containers have zIndex 0-1)
                        }}
                        className={cn(
                            'px-2 py-1 rounded border text-[10px] font-medium shadow-sm',
                            labelStyle.bg,
                            labelStyle.text,
                            labelStyle.border,
                            selected && 'ring-1 ring-blue-400'
                        )}
                    >
                        {edgeType === 'catch' && (
                            <div className="flex items-center gap-1">
                                <AlertTriangle size={10} />
                                <span>{label}</span>
                            </div>
                        )}
                        {isDefault && (
                            <div className="flex items-center gap-1">
                                <span className="text-[9px] opacity-75">DEFAULT</span>
                                <span>{label}</span>
                            </div>
                        )}
                        {isChoiceEdge && !isDefault && (() => {
                            const parsed = parseChoiceLabel(label);
                            if (parsed) {
                                return (
                                    <div className="flex flex-col items-center text-center">
                                        <span className="font-mono font-semibold text-[9px]">{parsed.variable}</span>
                                        <span className="opacity-80">{parsed.condition}</span>
                                    </div>
                                );
                            }
                            return <span>{label}</span>;
                        })()}
                        {isDynamicEdge && (
                            <div className="flex items-center gap-1">
                                <span className="text-[9px] opacity-75">DYNAMIC</span>
                                <span>{label}</span>
                            </div>
                        )}
                        {!isChoiceEdge && !isDynamicEdge && edgeType !== 'catch' && (
                            <span>{label}</span>
                        )}
                    </div>
                </EdgeLabelRenderer>
            )}

            {/* Show error indicator for catch edges without label */}
            {edgeType === 'catch' && !label && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            pointerEvents: 'all',
                            zIndex: 1000, // Ensure labels render above nodes (map containers have zIndex 0-1)
                        }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 text-[10px] font-medium"
                    >
                        <AlertTriangle size={10} />
                        <span>error</span>
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
});

/**
 * Create edge types registry for ReactFlow.
 */
export const edgeTypes = {
    workflow: WorkflowEdge,
};

